import { memo, useCallback, useMemo } from "react";

import type { ColumnPluginContext, GetColumnPluginsForSlot } from "../../plugins/types";
import type { SearchableSelectionOption } from "../../types";
import { NotSelectedColumnOptionView } from "../ColumnOptionView/ColumnOptionView";

type NotSelectedColumnOptionRowProps = {
    column: SearchableSelectionOption;
    searchTerm: string;
    getColumnPluginsForSlot?: GetColumnPluginsForSlot;
    onSelect: (columnId: string) => void;
};

/**
 * The memo boundary for a virtualised row. Scrolling re-renders the list on every range change, and
 * without this every visible row re-runs its plugin lookups and re-segments its label.
 *
 * The plugin context is built inside the memo on purpose: `getColumnPluginsForSlot` returns a fresh
 * array on each call, so a context object allocated by the caller would defeat the memo it feeds.
 */
const NotSelectedColumnOptionRowComponent = ({
    column,
    searchTerm,
    getColumnPluginsForSlot,
    onSelect,
}: NotSelectedColumnOptionRowProps) => {
    const { badgePlugins, actionPlugins } = useMemo(() => {
        const context: ColumnPluginContext = { column, isSelected: false };

        return {
            badgePlugins: getColumnPluginsForSlot?.("column-badge", context),
            actionPlugins: getColumnPluginsForSlot?.("column-action", context),
        };
    }, [column, getColumnPluginsForSlot]);

    const handleClick = useCallback(() => onSelect(column.id), [onSelect, column.id]);

    return (
        <NotSelectedColumnOptionView
            column={column}
            searchTerm={searchTerm}
            badgePlugins={badgePlugins}
            actionPlugins={actionPlugins}
            onClick={handleClick}
        />
    );
};

export const NotSelectedColumnOptionRow = memo(NotSelectedColumnOptionRowComponent);
